import React from 'react';
import { EyeIcons, FilePlusIcons, StarIcons } from '../../../../icons/icons';
import { NameEmailAvatar } from '../../../../ui/components/Common/NameEmailAvatar';
import {
  BankSearch,
  BankSearchPool,
  BankSearchResult,
} from '../../../../utils/types/candi_bank_types';
import { educationSummary } from '../../utils/bankSearchAdapters';

interface BankSearchResultsTableProps {
  results: BankSearchResult[];
  pool: BankSearchPool | null;
  search: BankSearch;
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onViewProfile: (result: BankSearchResult) => void;
  onInvite: (result: BankSearchResult) => void;
}

export const BankSearchResultsTable: React.FC<BankSearchResultsTableProps> = ({
  results,
  pool,
  search,
  total,
  page,
  limit,
  loading,
  onPageChange,
  onViewProfile,
  onInvite,
}) => {
  const totalPages = Math.ceil(total / limit);
  const startItem = total > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, total);

  const getPageNumbers = () => {
    const pages = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) {
      pages.push(i);
    }
    return pages;
  };

  const renderSkillChips = (result: BankSearchResult) => {
    interface ChipData {
      label: string;
      type: 'required' | 'mandatory' | 'desirable';
      status: 'matched' | 'missing';
      evidence?: string;
    }
    const chips: ChipData[] = [];

    // Required
    search.requiredSkills?.forEach((skill) => {
      const isMatched = result.matchedRequired?.includes(skill);
      chips.push({
        label: skill,
        type: 'required',
        status: isMatched ? 'matched' : 'missing',
        evidence: result.matchEvidence?.[skill],
      });
    });

    // Mandatory
    search.mandatorySkills?.forEach((skill) => {
      const isMatched = result.matchedMandatory?.includes(skill);
      chips.push({
        label: skill,
        type: 'mandatory',
        status: isMatched ? 'matched' : 'missing',
        evidence: result.matchEvidence?.[skill],
      });
    });

    // Desirable (only matched)
    result.matchedDesirable?.forEach((skill) => {
      chips.push({
        label: skill,
        type: 'desirable',
        status: 'matched',
        evidence: result.matchEvidence?.[skill],
      });
    });

    const displayChips = chips.slice(0, 6);
    const extraCount = chips.length - 6;

    return (
      <div className='flex flex-wrap gap-1 items-center'>
        {displayChips.map((chip, idx) => {
          let className = 'text-[11px] px-2 py-0.5 rounded-md font-medium whitespace-nowrap ';
          if (chip.type === 'required') {
            className += chip.status === 'matched'
              ? 'border border-emerald-500 text-emerald-700 bg-emerald-50/50'
              : 'border border-red-400 text-red-700 bg-red-50/40';
          } else if (chip.type === 'mandatory') {
            className += chip.status === 'matched'
              ? 'bg-emerald-100/80 text-emerald-800 border border-emerald-200/60'
              : 'bg-red-50 text-red-700 border border-red-200/60';
          } else if (chip.type === 'desirable') {
            className += 'bg-blue-50 text-blue-700 border border-blue-200/60';
          }

          const title =
            chip.status === 'matched' && chip.evidence
              ? `matched "${chip.evidence}"`
              : undefined;

          return (
            <span key={idx} className={className} title={title}>
              {chip.label}
            </span>
          );
        })}
        {extraCount > 0 && (
          <span className='text-[10px] px-1.5 py-0.5 rounded-md font-medium whitespace-nowrap bg-slate-100 text-slate-500'>
            +{extraCount} more
          </span>
        )}
      </div>
    );
  };

  const renderScoreBar = (result: BankSearchResult) => {
    const scoreVal = result.score || 0;
    const width = `${Math.min(100, Math.max(0, scoreVal * 100))}%`;
    const breakdownEntries = Object.entries(result.breakdown || {});
    const title =
      breakdownEntries.length > 0
        ? breakdownEntries
            .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${(v as number).toFixed(2)}`)
            .join(' · ')
        : `Score: ${scoreVal.toFixed(2)}`;

    return (
      <div className='w-28 group relative cursor-help' title={title}>
        <div className='flex items-center justify-between text-xs font-bold text-slate-700 mb-1'>
          <span className='text-indigo-600 font-mono'>{scoreVal.toFixed(2)}</span>
          <span className='text-[10px] text-slate-400 font-medium'>
            {Math.round(scoreVal * 100)}%
          </span>
        </div>
        <div className='h-1.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/60'>
          <div
            className='h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-300'
            style={{ width }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className='flex flex-col w-full min-w-0 border border-slate-200/80 rounded-2xl overflow-hidden bg-white shadow-xs'>
      <div className='overflow-auto max-h-[calc(100vh-320px)] relative'>
        {loading && (
          <div className='absolute inset-0 bg-white/60 backdrop-blur-2xs z-20 flex items-center justify-center'>
            <div className='w-7 h-7 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin' />
          </div>
        )}

        <table className='w-full border-collapse text-left text-xs'>
          {/* Header */}
          <thead className='sticky top-0 z-10 bg-slate-50/90 backdrop-blur-xs border-b border-slate-200/80'>
            <tr>
              <th className='px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider min-w-[220px]'>
                Candidate
              </th>
              <th className='px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider w-36'>
                Match Score
              </th>
              <th className='px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider'>
                Skills & Evidence
              </th>
              <th className='px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider w-28'>
                Actions
              </th>
            </tr>
          </thead>

          {/* Body */}
          <tbody className='divide-y divide-slate-100 bg-white'>
            {results.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className='px-4 py-16 text-center text-slate-500'>
                  {pool && pool.ranked === 0 ? (
                    <div className='max-w-md mx-auto space-y-1.5'>
                      <p className='text-sm font-bold text-slate-700'>No candidates passed the criteria</p>
                      <p className='text-xs text-slate-500 leading-relaxed'>
                        All {pool.people || 0} candidates are under the experience bar ({search.minimumWorkExperience} yrs) or missing hard requirements. Try lowering the experience threshold or moving skills to &ldquo;Important&rdquo;.
                      </p>
                    </div>
                  ) : (
                    <span className='text-xs text-slate-400'>No results found for this search.</span>
                  )}
                </td>
              </tr>
            )}

            {results.map((result, i) => {
              const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim() || 'Unknown Candidate';
              const education = educationSummary(result.educations);

              return (
                <tr
                  key={result.id || i}
                  onClick={() => onViewProfile(result)}
                  className='hover:bg-indigo-50/40 transition-colors cursor-pointer group'
                >
                  {/* Candidate Profile Info */}
                  <td className='px-4 py-3 align-middle'>
                    <div className='flex items-center gap-3'>
                      <NameEmailAvatar
                        name={fullName}
                        email={result.email || ''}
                        avatar=''
                        avatarWidth='34px'
                        avatarHeight='34px'
                        nameFontSize='13px'
                        emailFontSize='11px'
                      />
                      <div className='flex flex-wrap gap-1 items-center'>
                        <span title='AI Profile Match'>
                          <StarIcons className='w-3 h-3 text-purple-400 shrink-0' />
                        </span>

                        {result.alreadyApplied && (
                          <span className='px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-100 text-indigo-700 uppercase tracking-wider'>
                            Applied
                          </span>
                        )}

                        {result.years !== undefined && (
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                              result.datesVerified
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200/80'
                                : 'bg-slate-50 text-slate-500 border-slate-200'
                            }`}
                          >
                            {result.years}y {!result.datesVerified && '(unverified)'}
                          </span>
                        )}

                        {education && (
                          <span
                            className='px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 max-w-[220px] truncate'
                            title={education}
                          >
                            {education}
                          </span>
                        )}

                        {result.jobSeekingStatus && (
                          <span className='px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600'>
                            {result.jobSeekingStatus}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Match Score */}
                  <td className='px-4 py-3 align-middle'>
                    {renderScoreBar(result)}
                  </td>

                  {/* Skill Badges */}
                  <td className='px-4 py-3 align-middle'>
                    {renderSkillChips(result)}
                  </td>

                  {/* Actions */}
                  <td className='px-4 py-3 align-middle text-right' onClick={(e) => e.stopPropagation()}>
                    <div className='flex items-center justify-end gap-1'>
                      <button
                        onClick={() => onViewProfile(result)}
                        className='p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all'
                        title='View Profile'
                      >
                        <EyeIcons className='w-4 h-4' />
                      </button>
                      <button
                        onClick={() => onInvite(result)}
                        className='p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all'
                        title='Add to Opening / Invite List'
                      >
                        <FilePlusIcons className='w-4 h-4' />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer: Funnel stats & Pagination */}
      <div className='px-4 py-3 border-t border-slate-100 bg-slate-50/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500'>
        {/* Funnel Line */}
        {pool && (
          <div className='text-[11px] text-slate-500 flex items-center gap-1.5 font-medium'>
            <svg className='w-3.5 h-3.5 text-slate-400 flex-shrink-0' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <circle cx='12' cy='12' r='10' />
              <line x1='12' y1='16' x2='12' y2='12' />
              <line x1='12' y1='8' x2='12.01' y2='8' />
            </svg>
            <span>
              <strong className='text-slate-700'>{pool.ranked || 0}</strong> of <strong className='text-slate-700'>{pool.people || 0}</strong> people ranked · <strong className='text-slate-700'>{pool.gatedByExperience || 0}</strong> under experience bar · <strong className='text-slate-700'>{pool.unverified || 0}</strong> unverified dates
            </span>
          </div>
        )}

        {/* Pagination controls */}
        {total > 0 && (
          <div className='flex items-center gap-3 ml-auto'>
            <span className='text-[11px] font-medium'>
              Showing <strong className='text-slate-700'>{startItem}-{endItem}</strong> of <strong className='text-slate-700'>{total}</strong>
            </span>

            <div className='flex items-center space-x-1'>
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className='p-1 rounded-lg hover:bg-slate-200/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
              >
                <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <polyline points='15 18 9 12 15 6' />
                </svg>
              </button>

              {getPageNumbers().map((p) => (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`min-w-[24px] px-1.5 py-0.5 rounded-lg text-center font-bold text-xs transition-all ${
                    p === page
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'hover:bg-slate-200/70 text-slate-600'
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                className='p-1 rounded-lg hover:bg-slate-200/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
              >
                <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <polyline points='9 18 15 12 9 6' />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
